import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import TabNavigation from '../components/TabNavigation';
import '../styles/dashboard-pages.css';
import '../styles/community.css';

interface Tab {
  path: string;
  label: string;
  icon: string;
}

const communityTabs: Tab[] = [
  { path: '/dashboard/community/forum', label: 'Forum', icon: '💬' },
  { path: '/dashboard/community/leaderboards', label: 'Leaderboards', icon: '🏆' },
  { path: '/dashboard/community/models', label: 'Models', icon: '🤖' },
  { path: '/dashboard/community/pipelines', label: 'Pipelines', icon: '⚙️' },
  { path: '/dashboard/community/datasets', label: 'Datasets', icon: '📊' },
  { path: '/dashboard/community/co-work', label: 'Co-work', icon: '🤝' },
  { path: '/dashboard/community/challenges', label: 'Challenges', icon: '🎯' },
  { path: '/dashboard/community/resources', label: 'Resources', icon: '📚' },
];

const Forum: React.FC = () => (
  <div className="community-section">
    <h2>Forum</h2>
    <p className="section-subtitle">Discuss AI, machine learning, and share your ideas with the community</p>

    <div className="forum-content">
      <div className="forum-section-card">
        <h3>📌 Recent Discussions</h3>
        <div className="forum-threads">
          <div className="thread-item">
            <div className="thread-title">Best practices for model optimization?</div>
            <div className="thread-meta">
              <span className="thread-author">By: John Doe</span>
              <span className="thread-replies">5 replies</span>
            </div>
          </div>
          <div className="thread-item">
            <div className="thread-title">How to handle imbalanced datasets</div>
            <div className="thread-meta">
              <span className="thread-author">By: Jane Smith</span>
              <span className="thread-replies">12 replies</span>
            </div>
          </div>
          <div className="thread-item">
            <div className="thread-title">Share your favorite preprocessing tips</div>
            <div className="thread-meta">
              <span className="thread-author">By: Alex Chen</span>
              <span className="thread-replies">8 replies</span>
            </div>
          </div>
        </div>
      </div>

      <div className="forum-section-card">
        <h3>❓ Get Help</h3>
        <p>Ask questions and get help from the community. Someone will be happy to assist!</p>
        <button className="action-btn primary">Start a Discussion</button>
      </div>
    </div>
  </div>
);

const Leaderboards: React.FC = () => (
  <div className="community-section">
    <h2>Leaderboards</h2>
    <p className="section-subtitle">Compete and showcase your achievements</p>

    <div className="leaderboard-tabs">
      <button className="leaderboard-tab active">Weekly</button>
      <button className="leaderboard-tab">Monthly</button>
      <button className="leaderboard-tab">All Time</button>
    </div>

    <div className="leaderboard-content">
      <div className="leaderboard-table">
        <div className="table-header">
          <div className="rank">Rank</div>
          <div className="user">User</div>
          <div className="score">Score</div>
          <div className="models">Models</div>
        </div>
        {[1, 2, 3, 4, 5].map((rank) => (
          <div key={rank} className="table-row">
            <div className="rank">
              {rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : rank}
            </div>
            <div className="user">User {rank}</div>
            <div className="score">{(10000 - rank * 500).toLocaleString()}</div>
            <div className="models">{10 - rank * 2}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SharedModels: React.FC = () => (
  <div className="community-section">
    <h2>Community Models</h2>
    <p className="section-subtitle">Explore and use models shared by the community</p>

    <div className="search-bar">
      <input type="text" placeholder="Search models..." className="search-input" />
    </div>

    <div className="models-grid">
      {[1, 2, 3, 4, 5, 6].map((id) => (
        <div key={id} className="model-card">
          <div className="card-header">
            <div className="card-icon">🤖</div>
            <div className="card-badge">Popular</div>
          </div>
          <h3>ResNet Variant {id}</h3>
          <p className="card-description">A community-improved ResNet model with 94% accuracy on ImageNet</p>
          <div className="card-stats">
            <div className="stat">
              <span className="stat-icon">⭐</span>
              <span className="stat-value">4.{8 + id}</span>
            </div>
            <div className="stat">
              <span className="stat-icon">📥</span>
              <span className="stat-value">{(500 - id * 50).toLocaleString()}</span>
            </div>
          </div>
          <button className="action-btn secondary small">Use Model</button>
        </div>
      ))}
    </div>
  </div>
);

const SharedPipelines: React.FC = () => (
  <div className="community-section">
    <h2>Community Pipelines</h2>
    <p className="section-subtitle">Discover and use pipelines created by the community</p>

    <div className="search-bar">
      <input type="text" placeholder="Search pipelines..." className="search-input" />
    </div>

    <div className="pipelines-grid">
      {[1, 2, 3, 4, 5, 6].map((id) => (
        <div key={id} className="pipeline-card">
          <div className="card-header">
            <div className="card-icon">⚙️</div>
            <div className="card-badge">Trending</div>
          </div>
          <h3>Image Classification Pipeline {id}</h3>
          <p className="card-description">End-to-end pipeline for image classification with preprocessing and evaluation</p>
          <div className="card-stats">
            <div className="stat">
              <span className="stat-icon">⭐</span>
              <span className="stat-value">4.{7 + id}</span>
            </div>
            <div className="stat">
              <span className="stat-icon">📥</span>
              <span className="stat-value">{(300 - id * 30).toLocaleString()}</span>
            </div>
          </div>
          <button className="action-btn secondary small">Use Pipeline</button>
        </div>
      ))}
    </div>
  </div>
);

const SharedDatasets: React.FC = () => (
  <div className="community-section">
    <h2>Community Datasets</h2>
    <p className="section-subtitle">Access datasets shared and curated by the community</p>

    <div className="search-bar">
      <input type="text" placeholder="Search datasets..." className="search-input" />
    </div>

    <div className="datasets-grid">
      {[1, 2, 3, 4, 5, 6].map((id) => (
        <div key={id} className="dataset-card">
          <div className="card-header">
            <div className="card-icon">📊</div>
            <div className="card-badge">Quality</div>
          </div>
          <h3>Dataset {id}</h3>
          <p className="card-description">High-quality dataset for training and validation</p>
          <div className="card-stats">
            <div className="stat">
              <span className="stat-label">Size</span>
              <span className="stat-value">{(10 + id)}GB</span>
            </div>
            <div className="stat">
              <span className="stat-label">Samples</span>
              <span className="stat-value">{(1000 + id * 100).toLocaleString()}</span>
            </div>
          </div>
          <button className="action-btn secondary small">Download</button>
        </div>
      ))}
    </div>
  </div>
);

const CoWork: React.FC = () => (
  <div className="community-section">
    <h2>Co-work</h2>
    <p className="section-subtitle">Collaborate with other members on projects</p>

    <div className="cowork-content">
      <div className="cowork-section">
        <h3>🚀 Active Projects</h3>
        <div className="projects-list">
          {[1, 2, 3].map((id) => (
            <div key={id} className="project-item">
              <div className="project-info">
                <h4>Collaborative Project {id}</h4>
                <p>Description of the collaborative project and what we're working on</p>
                <div className="project-meta">
                  <span className="members">👥 {3 + id} members</span>
                  <span className="status">Status: In Progress</span>
                </div>
              </div>
              <button className="action-btn secondary small">Join Project</button>
            </div>
          ))}
        </div>
      </div>

      <div className="cowork-section">
        <h3>👥 Find Collaborators</h3>
        <p>Looking to collaborate? Post about your project needs and find team members</p>
        <button className="action-btn primary">Post a Project</button>
      </div>
    </div>
  </div>
);

const Challenges: React.FC = () => (
  <div className="community-section">
    <h2>Challenges</h2>
    <p className="section-subtitle">Participate in competitions and challenges</p>

    <div className="challenges-content">
      <div className="challenge-filters">
        <button className="filter-btn active">All</button>
        <button className="filter-btn">Active</button>
        <button className="filter-btn">Completed</button>
      </div>

      <div className="challenges-grid">
        {[1, 2, 3, 4].map((id) => (
          <div key={id} className="challenge-card">
            <div className="challenge-header">
              <h3>Challenge {id}: {id === 1 ? 'Image Recognition' : id === 2 ? 'Text Classification' : id === 3 ? 'Anomaly Detection' : 'Time Series Forecasting'}</h3>
              <span className="difficulty">{['Easy', 'Medium', 'Hard', 'Expert'][id - 1]}</span>
            </div>
            <p className="challenge-description">
              {id === 1 ? 'Build an image recognition model that achieves 95%+ accuracy' :
               id === 2 ? 'Classify text documents with maximum efficiency' :
               id === 3 ? 'Detect anomalies in sensor data streams' :
               'Forecast time series with minimal error'}
            </p>
            <div className="challenge-stats">
              <div className="stat">
                <span className="label">Participants</span>
                <span className="value">{(150 + id * 50).toLocaleString()}</span>
              </div>
              <div className="stat">
                <span className="label">Prize Pool</span>
                <span className="value">${(1000 + id * 500)}</span>
              </div>
            </div>
            <button className="action-btn primary small">Participate</button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Resources: React.FC = () => (
  <div className="community-section">
    <h2>Resources</h2>
    <p className="section-subtitle">Learning materials and documentation shared by the community</p>

    <div className="resources-content">
      <div className="resource-categories">
        <div className="category-card">
          <div className="category-icon">📖</div>
          <h3>Tutorials</h3>
          <p>Step-by-step guides on ML concepts</p>
          <span className="count">124 resources</span>
        </div>
        <div className="category-card">
          <div className="category-icon">🎥</div>
          <h3>Videos</h3>
          <p>Video tutorials and talks</p>
          <span className="count">89 resources</span>
        </div>
        <div className="category-card">
          <div className="category-icon">📚</div>
          <h3>Papers</h3>
          <p>Academic papers and research</p>
          <span className="count">256 resources</span>
        </div>
        <div className="category-card">
          <div className="category-icon">💻</div>
          <h3>Code Snippets</h3>
          <p>Useful code examples</p>
          <span className="count">342 resources</span>
        </div>
      </div>
    </div>
  </div>
);

const CommunityPage: React.FC = () => {
  const location = useLocation();

  const renderContent = () => {
    const pathname = location.pathname;

    if (pathname.includes('/forum')) return <Forum />;
    if (pathname.includes('/leaderboards')) return <Leaderboards />;
    if (pathname.includes('/models')) return <SharedModels />;
    if (pathname.includes('/pipelines')) return <SharedPipelines />;
    if (pathname.includes('/datasets')) return <SharedDatasets />;
    if (pathname.includes('/co-work')) return <CoWork />;
    if (pathname.includes('/challenges')) return <Challenges />;
    if (pathname.includes('/resources')) return <Resources />;

    // Default to forum if no specific tab
    return <Forum />;
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>👥 Community</h1>
        <p>Connect, collaborate, and learn with AI enthusiasts worldwide</p>
      </div>

      <div className="community-tabs-wrapper">
        <TabNavigation tabs={communityTabs} basePath="/dashboard/community" />
      </div>

      <div className="community-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default CommunityPage;
